import { LEARNING_CONTENT_SEED } from '../data/learningContentSeed';
import {
  clampExamCount,
  clampPassPercent,
  isLevelBand,
  PRACTICE_EXAM_PASS_PERCENT,
  PRACTICE_EXAM_QUESTIONS,
  type LevelBand,
} from './practiceModel';
import { normalizeDiagram, normalizeSections } from './roadDiagram';
import { supabase } from './supabaseClient';
import type { ContentCard, ContentPath } from './contentRecords';

const PRACTICE_SELECT = `
  id, slug, exam_question_count, exam_pass_percent,
  learning_modules (
    id, slug, level_band,
    learning_lessons ( id, slug, cards, sections, practice_topic )
  )
`;

function asCards(value: unknown): ContentCard[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((card) => {
      const row = card as ContentCard;
      return {
        image_url: row?.image_url || undefined,
        diagram: normalizeDiagram(row?.diagram),
        caption_en: String(row?.caption_en || ''),
        caption_my: String(row?.caption_my || ''),
        lines_en: Array.isArray(row?.lines_en) ? row.lines_en.map(String) : [],
        lines_my: Array.isArray(row?.lines_my) ? row.lines_my.map(String) : [],
        key_en: String(row?.key_en || ''),
        key_my: String(row?.key_my || ''),
        takeaway_en: String(row?.takeaway_en || ''),
        takeaway_my: String(row?.takeaway_my || ''),
      };
    })
    .filter((card) => card.lines_en.length > 0 || card.lines_my.length > 0 || card.key_en || card.key_my);
}

function withDefaults(path: ContentPath): ContentPath {
  return {
    ...path,
    exam_question_count: clampExamCount(path.exam_question_count ?? PRACTICE_EXAM_QUESTIONS),
    exam_pass_percent: clampPassPercent(path.exam_pass_percent ?? PRACTICE_EXAM_PASS_PERCENT),
    modules: path.modules.map((module) => ({
      ...module,
      level_band: isLevelBand(module.level_band) ? module.level_band : null,
      lessons: module.lessons.map((lesson) => ({
        ...lesson,
        cards: lesson.cards || [],
        sections: lesson.sections || [],
        practice_topic: lesson.practice_topic || null,
      })),
    })),
  };
}

function overlayFromSeed(paths: ContentPath[]): ContentPath[] {
  return paths.map((path) => {
    const seed = LEARNING_CONTENT_SEED.find((item) => item.slug === path.slug);
    if (!seed) return withDefaults(path);
    const modules = path.modules.map((module) => {
      const seedModule = seed.modules.find((item) => item.slug === module.slug);
      return {
        ...module,
        level_band: isLevelBand(seedModule?.level_band) ? seedModule?.level_band || null : null,
        lessons: module.lessons.map((lesson) => {
          const seedLesson = seedModule?.lessons.find((item) => item.slug === lesson.slug);
          return {
            ...lesson,
            cards: asCards(seedLesson?.cards || []),
            sections: normalizeSections(seedLesson?.sections || []),
            practice_topic: seedLesson?.practice_topic || null,
          };
        }),
      };
    });
    return {
      ...path,
      exam_question_count: clampExamCount(seed.exam_question_count ?? PRACTICE_EXAM_QUESTIONS),
      exam_pass_percent: clampPassPercent(seed.exam_pass_percent ?? PRACTICE_EXAM_PASS_PERCENT),
      modules,
    };
  });
}

function applyRow(path: ContentPath, row: Record<string, unknown>): ContentPath {
  const modulesRaw = (row.learning_modules || []) as Record<string, unknown>[];
  const byModule = new Map(modulesRaw.map((module) => [String(module.id), module]));
  return {
    ...path,
    exam_question_count: clampExamCount(row.exam_question_count == null ? PRACTICE_EXAM_QUESTIONS : Number(row.exam_question_count)),
    exam_pass_percent: clampPassPercent(row.exam_pass_percent == null ? PRACTICE_EXAM_PASS_PERCENT : Number(row.exam_pass_percent)),
    modules: path.modules.map((module) => {
      const raw = byModule.get(module.id);
      if (!raw) {
        return {
          ...module,
          level_band: null,
          lessons: module.lessons.map((lesson) => ({
            ...lesson,
            cards: lesson.cards || [],
            sections: lesson.sections || [],
            practice_topic: lesson.practice_topic || null,
          })),
        };
      }
      const lessonsRaw = (raw.learning_lessons || []) as Record<string, unknown>[];
      const byLesson = new Map(lessonsRaw.map((lesson) => [String(lesson.id), lesson]));
      return {
        ...module,
        level_band: isLevelBand(raw.level_band) ? (raw.level_band as LevelBand) : null,
        lessons: module.lessons.map((lesson) => {
          const rawLesson = byLesson.get(lesson.id);
          if (!rawLesson) {
            return {
              ...lesson,
              cards: lesson.cards || [],
              sections: lesson.sections || [],
              practice_topic: lesson.practice_topic || null,
            };
          }
          return {
            ...lesson,
            cards: asCards(rawLesson.cards),
            sections: normalizeSections(rawLesson.sections),
            practice_topic: rawLesson.practice_topic ? String(rawLesson.practice_topic) : null,
          };
        }),
      };
    }),
  };
}

export async function withPracticeFields(paths: ContentPath[]): Promise<{ paths: ContentPath[]; source: 'database' | 'seed' }> {
  if (paths.length === 0 || !import.meta.env.VITE_SUPABASE_URL) {
    return { paths: overlayFromSeed(paths), source: 'seed' };
  }
  const { data, error } = await supabase.from('learning_paths').select(PRACTICE_SELECT).in('id', paths.map((path) => path.id));
  if (error || !data) return { paths: overlayFromSeed(paths), source: 'seed' };
  const byId = new Map((data as unknown as Record<string, unknown>[]).map((row) => [String(row.id), row]));
  return {
    paths: paths.map((path) => {
      const row = byId.get(path.id);
      return row ? applyRow(path, row) : withDefaults(path);
    }),
    source: 'database',
  };
}
